import { useEffect, useState } from 'react';
import jwtDecode from 'jwt-decode';
import { bindPromiseCreators } from 'redux-saga-routines';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SOURCE_EMAIL, SOURCE_SMS } from '@whitelabel/xcover-shared/helpers/constants';
import { ICustomer } from '@whitelabel/helpers/types';
import { captureExceptionWithFullStory } from '@whitelabel/helpers/utils';
import { checkCustomerPromiseCreator } from '../../../actions';

interface ISignUpToken {
  exp: number;
  source: string;
  phone: string;
  email: string;
  from: string;
  to: string;
}

export function useSignUpToken(signUpToken: string | null, locale: string, token: string | null) {
  const [maskedSignUpTokenValid, setSignUpTokenValid] = useState<boolean | undefined>();
  const [tokenSource, setTokenSource] = useState<string>();
  const [jwtCustomer, setJWTCustomer] = useState<ICustomer>();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    const { checkCustomer } = bindPromiseCreators({ checkCustomer: checkCustomerPromiseCreator }, dispatch);
    if (signUpToken) {
      const { exp, source, phone, email, to } = jwtDecode<ISignUpToken>(signUpToken);
      const checkIfAlreadySignedUp = async (requestValue: string, isEmail: boolean): Promise<void> => {
        try {
          const customer = await checkCustomer({ requestValue, isEmail, ignoreError: true });
          if ((isEmail && customer.emailVerified) || (!isEmail && customer.phoneVerified)) {
            history.push(`/${locale}/login?source=${isEmail ? SOURCE_EMAIL : SOURCE_SMS}`);
          }
        } catch (e) {
          // need to suppress error since checkCustomer will 404 here
          if (phone || email) {
            setJWTCustomer({ id: to, phone, email });
          }
        }
      };

      try {
        setTokenSource(source);
        if (exp * 1000 < Date.now()) {
          setSignUpTokenValid(false);
          const signupTokenError = new Error('signupToken expired');
          captureExceptionWithFullStory(signupTokenError);
        } else {
          const isEmail = source === SOURCE_EMAIL;
          checkIfAlreadySignedUp(isEmail ? email : phone, isEmail);
          setSignUpTokenValid(true);
        }
      } catch {
        setSignUpTokenValid(false);
      }
    } else if (!token) {
      setSignUpTokenValid(false);
    }
  }, [history, locale, signUpToken, dispatch, token]);

  return [tokenSource, maskedSignUpTokenValid, jwtCustomer] as const;
}
